<template>
  <div class="app-terms-acceptance">
    <AppLogo size="lg" full class="app-terms-acceptance__logo" />
    <p class="app-terms-acceptance__intro">
      Please review the updated Terms of Service. You must read the
      full document and accept the terms before continuing to use Allposit.
    </p>
    <div class="app-terms-acceptance__terms">
      <TermsContent />
      <div ref="termsEndRef" />
    </div>
    <div class="app-terms-acceptance__confirmation">
      <UICheckbox
        v-model="confirmed"
        :disabled="!canAccept"
      >
        I have read and agree to the updated Terms of Service.
      </UICheckbox>
      <UIButtonsGroup>
        <UIButton
          label="Sign out"
          variant="gray"
          fill="outlined"
          :disabled="loading"
          @click="emit('signOut')"
        />
        <UIButton
          label="Accept Terms"
          :disabled="!canAccept || !confirmed"
          :loading="loading"
          @click="emit('accept')"
        />
      </UIButtonsGroup>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, useTemplateRef, watchEffect } from 'vue'
import { useElementVisibility } from '@vueuse/core'

import TermsContent from '@/views/Legal/TermsContent.vue'

import { UIButton, UIButtonsGroup } from '@ui/buttons'
import { UICheckbox } from '@ui/controllers'
import { AppLogo } from '@app'

type Props = {
  loading?: boolean
}

type Emits = {
  accept: []
  signOut: []
}

defineProps<Props>()
const emit = defineEmits<Emits>()

const termsEndRef = useTemplateRef('termsEndRef')
const isTermsEndVisible = useElementVisibility(termsEndRef)

const confirmed = ref(false)
const canAccept = ref(false)

watchEffect(() => {
  if (!isTermsEndVisible.value) return
  canAccept.value = true
})
</script>

<style scoped>
.app-terms-acceptance {
  @apply h-[100dvh] max-w-6xl;
  @apply flex flex-col;
  @apply gap-6 p-6 mx-auto;

  &__logo {
    @apply shrink-0;
    @apply text-primary;
  }

  &__intro {
    @apply text-sm;
    @apply text-center;
  }

  &__terms {
    @apply flex-auto;
    @apply overflow-y-auto;
    @apply rounded-md border border-common-border;
    @apply bg-body-gray;
    @apply p-4 sm:p-6;
  }

  &__confirmation {
    @apply flex flex-col sm:flex-row gap-6 sm:gap-2 sm:items-center justify-between;

    .ui-button {
      @apply flex-auto sm:flex-none;
    }
  }
}
</style>
